/**
 * Bedrock Embeddings
 *
 * OpenAI-shape wrapper around AWS Bedrock embedding models, called via plain
 * HTTPS with AWS_BEARER_TOKEN_BEDROCK (no SigV4 needed for standard
 * InvokeModel).
 *
 * Primary responsibilities:
 * - Provide an `embeddings.create({ input, model, dimensions? })` surface that
 *   mirrors the OpenAI SDK so existing call sites don't need to change.
 * - Default to Cohere Embed v4 @ 1536 dims — drop-in for OpenAI
 *   `text-embedding-3-small` with NO re-embedding needed on existing DB
 *   vectors. Titan Text v2 (1024 dims) is available as a fallback for cost.
 * - Batch inputs when callers pass string[].
 * - Return `{ data: [{ embedding, index }], model, usage }`.
 *
 * Dependencies:
 * - global fetch (Node 20+)
 */

export const BEDROCK_DEFAULT_REGION =
  process.env.AWS_REGION || process.env.AWS_DEFAULT_REGION || 'us-east-1'
const DEFAULT_REGION = BEDROCK_DEFAULT_REGION

export const COHERE_EMBED_V4_MODEL = 'cohere.embed-v4:0'
export const TITAN_EMBED_V2_MODEL = 'amazon.titan-embed-text-v2:0'

// Default matches OpenAI text-embedding-3-small dims so existing 1536-dim
// vectors in Postgres still cosine-match against fresh embeddings.
export const BEDROCK_EMBEDDING_MODEL = COHERE_EMBED_V4_MODEL
export const BEDROCK_EMBEDDING_DIMENSIONS = 1536

// Real OpenAI fallback. text-embedding-3-small is natively 1536-dim — the SAME
// dimension as the stored vectors and as Cohere Embed v4 @ 1536 — so a query
// embedded by OpenAI cosine-matches the existing openai_embedding column with
// NO re-embedding. This is a genuinely different provider with a SEPARATE
// quota, so when Bedrock Cohere hits its daily token limit (429 "Too many
// tokens per day"), retrieval/upload transparently switch here instead of
// hard-failing. Only used when OPENAI_API_KEY is set.
const OPENAI_EMBEDDING_FALLBACK_MODEL = 'text-embedding-3-small'
const OPENAI_EMBEDDING_FALLBACK_DIMENSIONS = 1536

function hasOpenAIEmbeddingFallback() {
  return Boolean(process.env.OPENAI_API_KEY?.trim())
}

async function openAIEmbedBatch(texts: string[]): Promise<number[][]> {
  const { default: OpenAI } = await import('openai')
  const client = new OpenAI({ apiKey: process.env.OPENAI_API_KEY })
  const res = await client.embeddings.create({
    model: OPENAI_EMBEDDING_FALLBACK_MODEL,
    input: texts,
    dimensions: OPENAI_EMBEDDING_FALLBACK_DIMENSIONS
  })
  // Preserve input order.
  return res.data
    .slice()
    .sort((a, b) => a.index - b.index)
    .map((d) => d.embedding as number[])
}

// A Bedrock embedding failure worth switching providers for: daily/rate quota
// (429) or throttling. Dimension mismatches etc. are not retried on OpenAI.
function isBedrockQuotaFailure(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error ?? '')
  return /\b429\b|too many tokens|throttl|quota|rate ?limit/i.test(msg)
}

// Shared by lib/bedrock/mantle-client.ts (Bedrock chat models) — same bearer
// token, same "throw if missing" semantics.
export function getBedrockBearerToken() {
  const token = process.env.AWS_BEARER_TOKEN_BEDROCK?.trim()
  if (!token) throw new Error('AWS_BEARER_TOKEN_BEDROCK is not set')
  return token
}

async function bedrockInvoke(modelId: string, payload: unknown) {
  const url = `https://bedrock-runtime.${DEFAULT_REGION}.amazonaws.com/model/${encodeURIComponent(modelId)}/invoke`
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${getBedrockBearerToken()}`,
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    body: JSON.stringify(payload)
  })
  if (!res.ok) {
    const body = await res.text().catch(() => '')
    throw new Error(`Bedrock ${modelId} failed ${res.status}: ${body.slice(0, 400)}`)
  }
  return res.json()
}

type CohereEmbedResponse = {
  id: string
  texts: string[]
  embeddings: { float: number[][] } | number[][]
  response_type?: string
}
type TitanEmbedResponse = {
  embedding: number[]
  inputTextTokenCount?: number
}

// Cohere Embed v4 accepts at most 96 texts per InvokeModel call (AWS docs:
// https://docs.aws.amazon.com/bedrock/latest/userguide/model-parameters-embed-v4.html).
// Callers (app/api/upload, app/api/retrieval/process) batch up to 1000 texts
// per embeddings.create() call, matching OpenAI's much higher batch limit -
// forwarding that straight through as one `texts` array is what actually
// produced Bedrock's generic 400 "Invalid parameter combination." error.
const COHERE_MAX_TEXTS_PER_CALL = 96
const COHERE_BATCH_CONCURRENCY = 4

async function cohereEmbedOneCall(texts: string[], model: string, dimensions: number) {
  const payload = {
    texts,
    input_type: 'search_document' as const,
    output_dimension: dimensions,
    embedding_types: ['float'] as const
  }
  const j = (await bedrockInvoke(model, payload)) as CohereEmbedResponse
  return Array.isArray(j.embeddings) ? (j.embeddings as number[][]) : j.embeddings.float
}

// Cohere Embed v4 supports 256/512/1024/1536 dims. Batch-capable natively
// (up to COHERE_MAX_TEXTS_PER_CALL per call - sub-batched here so callers
// can keep passing arbitrarily large arrays).
async function cohereEmbedBatch(texts: string[], model: string, dimensions: number) {
  if (texts.length <= COHERE_MAX_TEXTS_PER_CALL) {
    return cohereEmbedOneCall(texts, model, dimensions)
  }

  const chunks: string[][] = []
  for (let i = 0; i < texts.length; i += COHERE_MAX_TEXTS_PER_CALL) {
    chunks.push(texts.slice(i, i + COHERE_MAX_TEXTS_PER_CALL))
  }

  const results: number[][][] = new Array(chunks.length)
  let cursor = 0
  async function worker() {
    while (true) {
      const idx = cursor++
      if (idx >= chunks.length) return
      results[idx] = await cohereEmbedOneCall(chunks[idx], model, dimensions)
    }
  }
  await Promise.all(
    Array.from({ length: Math.min(COHERE_BATCH_CONCURRENCY, chunks.length) }, () => worker())
  )

  return results.flat()
}

// Titan v2 is per-input (no batch), 256/512/1024. Fall back for cost.
async function titanEmbedOne(text: string, model: string, dimensions: number) {
  const j = (await bedrockInvoke(model, {
    inputText: text,
    dimensions,
    normalize: true
  })) as TitanEmbedResponse
  return { embedding: j.embedding, tokens: j.inputTextTokenCount ?? 0 }
}

export type BedrockEmbeddingResult = {
  object: 'list'
  data: { object: 'embedding'; embedding: number[]; index: number }[]
  model: string
  usage: { prompt_tokens: number; total_tokens: number }
}

export const bedrockEmbeddings = {
  async create(params: {
    input: string | string[]
    model?: string
    dimensions?: number
    encoding_format?: 'float' | 'base64'
    user?: string
  }): Promise<BedrockEmbeddingResult> {
    const inputs = Array.isArray(params.input) ? params.input : [params.input]
    // This wrapper mirrors the OpenAI SDK so call sites can keep passing an
    // OpenAI-style model name (e.g. `text-embedding-3-small`) unchanged —
    // that name isn't a real Bedrock model id, so only honor `params.model`
    // when it's actually a recognized Bedrock embedding model; otherwise
    // fall back to the real default instead of forwarding an invalid id to
    // Bedrock (previously caused "The provided model identifier is invalid").
    const isRecognizedBedrockModel = (model: string) =>
      model.startsWith('cohere.') || model.startsWith('amazon.titan-embed')
    const requestedModel =
      params.model && isRecognizedBedrockModel(params.model)
        ? params.model
        : BEDROCK_EMBEDDING_MODEL
    const isCohere = requestedModel.startsWith('cohere.')
    const isTitan = requestedModel.startsWith('amazon.titan-embed')

    // Cohere supports 256/512/1024/1536; Titan supports 256/512/1024
    const dimensions = params.dimensions ?? BEDROCK_EMBEDDING_DIMENSIONS

    if (isCohere || (!isTitan && requestedModel === BEDROCK_EMBEDDING_MODEL)) {
      let vectors: number[][]
      let effectiveModel = requestedModel
      try {
        vectors = await cohereEmbedBatch(inputs, requestedModel, dimensions)
      } catch (cohereError) {
        // Transparent provider switch on Bedrock quota/throttle: real OpenAI
        // text-embedding-3-small is also 1536-dim, so the vectors remain
        // compatible with the stored openai_embedding column. Only switch at
        // 1536 dims and only when a key is configured; otherwise rethrow.
        if (
          dimensions === OPENAI_EMBEDDING_FALLBACK_DIMENSIONS &&
          hasOpenAIEmbeddingFallback() &&
          isBedrockQuotaFailure(cohereError)
        ) {
          vectors = await openAIEmbedBatch(inputs)
          effectiveModel = OPENAI_EMBEDDING_FALLBACK_MODEL
        } else {
          throw cohereError
        }
      }
      return {
        object: 'list',
        data: vectors.map((embedding, index) => ({
          object: 'embedding',
          embedding,
          index
        })),
        model: effectiveModel,
        // Cohere on Bedrock doesn't return token counts on this shape; leave zero
        usage: { prompt_tokens: 0, total_tokens: 0 }
      }
    }

    // Titan v2 path — one-at-a-time with concurrency
    const CONCURRENCY = 8
    const results: Array<{ embedding: number[]; tokens: number }> = new Array(
      inputs.length
    )
    let cursor = 0
    async function worker() {
      while (true) {
        const idx = cursor++
        if (idx >= inputs.length) return
        results[idx] = await titanEmbedOne(inputs[idx], requestedModel, dimensions)
      }
    }
    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, inputs.length) }, () => worker())
    )
    let totalTokens = 0
    const data = results.map((r, index) => {
      totalTokens += r.tokens
      return { object: 'embedding' as const, embedding: r.embedding, index }
    })
    return {
      object: 'list',
      data,
      model: requestedModel,
      usage: { prompt_tokens: totalTokens, total_tokens: totalTokens }
    }
  }
}

export function hasBedrockEmbeddingsAuth() {
  return Boolean(process.env.AWS_BEARER_TOKEN_BEDROCK?.trim())
}
